import React from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { SignupStatus } from 'features/frictionless-signup/services/signupApi';

interface RunnerStatusDisplayProps {
  /** Current signup status */
  status: SignupStatus | null;
  /** Optional custom className for the container */
  className?: string;
}

/**
 * RunnerStatusDisplay Component
 *
 * Displays the current runner provisioning status based on the signup status.
 * Provides visual feedback for different states in the runner provisioning process.
 *
 * @component
 */
const RunnerStatusDisplay: React.FC<RunnerStatusDisplayProps> = ({ status, className = '' }) => {
  if (!status) {
    return null;
  }

  // Determine display based on signup status
  switch (status) {
    case SignupStatus.SIGNUP_CREATED:
      return (
        <div className={`text-center py-8 ${className}`}>
          <ClockIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-sm text-secondary-text">Waiting for Snowflake account to be connected</p>
        </div>
      );

    case SignupStatus.SNOWFLAKE_ACCOUNT_CREATED:
      return (
        <div className={`text-center py-8 ${className}`}>
          <CheckCircleIcon className="h-12 w-12 mx-auto text-green-highlight mb-3" />
          <p className="text-sm text-dataops-dark mb-1">Snowflake account connected</p>
          <p className="text-xs text-secondary-text">Ready to provision runner</p>
        </div>
      );

    case SignupStatus.RUNNER_PROVISIONING:
      return (
        <div className={`text-center py-8 ${className}`}>
          <div className="mx-auto flex items-center justify-center h-12 w-12 mb-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dataops-primary-blue"></div>
          </div>
          <p className="text-sm text-dataops-dark mb-1">Getting runner setup...</p>
          <p className="text-xs text-secondary-text">This may take a few moments</p>
        </div>
      );

    case SignupStatus.RUNNER_PROVISIONING_FAILED:
      return (
        <div className={`text-center py-8 ${className}`}>
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-red-highlight mb-3" />
          <p className="text-sm text-dataops-dark mb-1">Runner provisioning failed</p>
          <p className="text-xs text-secondary-text mb-3">
            Please contact{' '}
            <a href="mailto:support@dataops.live" className="text-dataops-primary-blue hover:text-hover-primary-blue">
              support@dataops.live
            </a>{' '}
            for assistance
          </p>
          <p className="text-xs text-red-900">You will need a runner to use the platform</p>
        </div>
      );

    case SignupStatus.RUNNER_PROVISIONED:
    case SignupStatus.SIGNUP_COMPLETED:
      return (
        <div className={`text-center py-8 ${className}`}>
          <CheckCircleIcon className="h-12 w-12 mx-auto text-green-highlight mb-3" />
          <p className="text-sm text-dataops-dark">Runner is ready</p>
        </div>
      );

    default:
      return null;
  }
};

export default RunnerStatusDisplay;
